import React from 'react';
import moment, { type MomentInput } from 'moment';
import { FormattedMessage } from 'react-intl';
import { Grid } from '@mui/material';

import { Utils } from 'app/main/components';
import { useSettings, useUserData } from 'app/main/components/hooks';
import {
	DateField,
	ModuleSearchField,
	NumberTextField,
	SearchSelectField,
	SimpleTextField,
	TimeField
} from 'app/main/components/fields';
import { type DetailsSectionInfoContentProps } from 'app/main/components/page-layouts';

export default function WorkReportsDetailsSectionInfo(props: DetailsSectionInfoContentProps) {
	const { t } = useSettings();
	const userData = useUserData();

	function calcDuration(from: MomentInput, to: MomentInput) {
		if (from && to && !props.model.wore_length_hours && !props.model.wore_length_minutes) {
			const maxDuration = moment.duration(moment.utc(to).diff(moment.utc(from)));
			if ((maxDuration.asMinutes(), 10) > 0) {
				const lenghtHours = Math.floor(maxDuration.asHours());
				const lenghtMinutes = Math.floor(maxDuration.asMinutes()) % 60;
				props.sendChange('wore_length_hours', lenghtHours > 0 ? lenghtHours : null);
				props.sendChange('wore_length_minutes', lenghtMinutes > 0 ? lenghtMinutes : null);
			}
		}
	}

	return (
		<>
			<Grid size={12}>
				{/* TITLE */}
				<SimpleTextField
					name="wore_title"
					label={t('DETAILS_WORKREPORTS_FIELD_TITLE')}
					value={props.model.wore_title}
					isEdit={props.isEdit}
					logProps={props.logProps}
					sendChange={props.sendChange}
					required
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			<Grid size={{ sm: 6, xs: 12 }}>
				{/* STATUS */}
				<SearchSelectField
					name="wore_wost_id"
					label={t('DETAILS_WORKREPORTS_FIELD_STATUS')}
					systable="workreports_statuses"
					value={props.model.wore_wost_id}
					isEdit={props.isEdit}
					logProps={props.logProps}
					sendChange={props.sendChange}
				/>
			</Grid>
			<Grid size={{ sm: 6, xs: 12 }}>
				{/* TYPE */}
				<SearchSelectField
					name="wore_woty_id"
					label={t('DETAILS_WORKREPORTS_FIELD_TYPE')}
					systable="workreports_types"
					value={props.model.wore_woty_id}
					isEdit={props.isEdit}
					logProps={props.logProps}
					sendChange={props.sendChange}
					afterChange={newValue => {
						if (!props.model.wore_title || props.model.wore_title === props.model.woty_title)
							props.sendChange('wore_title', newValue.woty_title);
						props.sendChange('woty_title', newValue.woty_title);
					}}
				/>
			</Grid>
			<Grid size={{ sm: 6, xs: 12 }}>
				{/* COMPANY */}
				<ModuleSearchField
					name="wore_comp_uid"
					label={t('DETAILS_WORKREPORTS_FIELD_COMPANY')}
					module="companies"
					uid={props.model.wore_comp_uid}
					isEdit={props.isEdit}
					logProps={props.logProps}
					sendChange={props.sendChange}
				/>
			</Grid>
			<Grid size={{ sm: 6, xs: 12 }}>
				{/* ASSIGNED USER */}
				<ModuleSearchField
					name="wore_user_assigned_uid"
					label={t('DETAILS_WORKREPORTS_FIELD_ASSIGNED_USER')}
					module="users"
					uid={props.model.wore_user_assigned_uid}
					isEdit={props.isEdit && !userData.user_role_external}
					logProps={props.logProps}
					sendChange={props.sendChange}
				/>
			</Grid>
			<Grid size={{ sm: 2, xs: 12 }}>
				{/* DATE */}
				<DateField
					name="wore_date"
					label={t('DETAILS_WORKREPORTS_FIELD_DATE')}
					value={props.model.wore_date}
					isEdit={props.isEdit}
					logProps={props.logProps}
					sendChange={props.sendChange}
					required
					validationErrors={{ isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED') }}
				/>
			</Grid>
			<Grid size={{ sm: 2, xs: 6 }}>
				{/* HOUR FROM */}
				<TimeField
					name="wore_hour_from"
					label={t('DETAILS_WORKREPORTS_FIELD_HOUR_FROM')}
					value={props.model.wore_hour_from}
					isEdit={props.isEdit}
					logProps={props.logProps}
					sendChange={props.sendChange}
					afterChange={newDate => calcDuration(newDate, props.model.wore_hour_to)}
					validations={{ maxTime: props.model.wore_hour_to }}
					validationErrors={{
						maxTime: (
							<FormattedMessage
								id="maxTimeError"
								defaultMessage={t('ERROR_MAX_TIME')}
								values={{ value: Utils.parseDate(props.model.wore_hour_to)?.format('HH:mm') }}
							/>
						)
					}}
				/>
			</Grid>
			<Grid size={{ sm: 2, xs: 6 }}>
				{/* HOUR TO */}
				<TimeField
					name="wore_hour_to"
					label={t('DETAILS_WORKREPORTS_FIELD_HOUR_TO')}
					value={props.model.wore_hour_to}
					isEdit={props.isEdit}
					logProps={props.logProps}
					sendChange={props.sendChange}
					afterChange={newDate => calcDuration(props.model.wore_hour_from, newDate)}
					validations={{ minTime: props.model.wore_hour_from }}
					validationErrors={{
						minTime: (
							<FormattedMessage
								id="minTimeError"
								defaultMessage={t('ERROR_MIN_TIME')}
								values={{ value: Utils.parseDate(props.model.wore_hour_from)?.format('HH:mm') }}
							/>
						)
					}}
				/>
			</Grid>
			<Grid size={{ sm: 3, xs: 6 }}>
				{/* TOT HOURS */}
				<NumberTextField
					name="wore_length_hours"
					label={t('DETAILS_WORKREPORTS_FIELD_LENGTH_HOURS')}
					value={props.model.wore_length_hours}
					isEdit={props.isEdit}
					logProps={props.logProps}
					measureUnit="h"
					sendChange={props.sendChange}
					validations={{ isInt: true }}
					validationErrors={{ isInt: t('ERROR_NUMBER_ONLY_INT') }}
				/>
			</Grid>
			<Grid size={{ sm: 3, xs: 6 }}>
				{/* TOT MINUTES */}
				<NumberTextField
					name="wore_length_minutes"
					label={t('DETAILS_WORKREPORTS_FIELD_LENGTH_MINUTES')}
					value={props.model.wore_length_minutes}
					isEdit={props.isEdit}
					logProps={props.logProps}
					measureUnit="min"
					sendChange={props.sendChange}
					validations={{ isInt: true }}
					validationErrors={{ isInt: t('ERROR_NUMBER_ONLY_INT') }}
				/>
			</Grid>
		</>
	);
}
